import { AspectRatio } from "@chakra-ui/react";
import BottleLabel from "models/bottle-label";
import { useEffect, useRef } from "react";
import { useMedia } from "react-use";

const backgroundWhisky: string = require("assets/images/labels/preview_whisky.jpg");
const backgroundGin: string = require("assets/images/labels/preview_gin.jpg");
const blankLabel: string = require("assets/images/bottle-label-blank_HC.png");
const oldBoxLabel: string = require("assets/images/bottle-label-blank_BOX.jpg");
const ginLabel: string = require("assets/images/ginetikett.png");

interface LabelSummaryProps {
  src?: string;
  isGin: boolean;
  label: BottleLabel;
  index: number;
}

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const GinSummary = ({
  isMobile,
  src,
  index,
}: {
  isMobile: boolean;
  index: number;
  src?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const drawCanvas = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Force minimum height of 1200px for high-res export
      const minHeight = 2000;
      const aspectRatio = 2.5;

      // Calculate width based on minimum height and aspect ratio
      const width = minHeight / aspectRatio;
      const height = minHeight;

      // Set canvas size directly (no DPR scaling needed since we're forcing high-res)
      canvas.width = width;
      canvas.height = height;

      try {
        // Load all images
        const [bgImg, labelImg, overlayImg] = await Promise.all([
          loadImage(backgroundGin),
          src ? loadImage(src) : null,
          loadImage(ginLabel.toString()),
        ]);

        // Draw background
        ctx.drawImage(bgImg, 0, 0, width, height);

        // Draw gin label overlay at 64% width, 50% top
        const overlayWidth = width * 0.64;
        const overlayHeight =
          (overlayImg.height / overlayImg.width) * overlayWidth;
        const overlayX = (width - overlayWidth) / 2;
        const overlayY = height * 0.51;
        ctx.drawImage(
          overlayImg,
          overlayX,
          overlayY,
          overlayWidth,
          overlayHeight
        );

        // Draw user label at 50.5% width, 50.5% top
        if (labelImg) {
          const labelWidth = width * 0.505;
          const labelHeight = (labelImg.height / labelImg.width) * labelWidth;
          const labelX = (width - labelWidth) / 2;
          const labelY = height * 0.495;
          ctx.drawImage(
            labelImg,
            labelX * 0.99,
            labelY * 1.035,
            labelWidth,
            labelHeight
          );
        }
      } catch (error) {
        console.error("Error loading images:", error);
      }
    };

    drawCanvas();
  }, [src]);

  return (
    <AspectRatio ratio={1 / 2.5} w={isMobile ? "full" : "300px"}>
      <canvas
        className="label-preview-canvas"
        id={`label-preview-canvas-${index}`}
        ref={canvasRef}
        style={{ width: "100%", height: "100%" }}
      />
    </AspectRatio>
  );
};

const WhiskySummary = ({
  isMobile,
  label,
  src,
  index,
}: {
  isMobile: boolean;
  label: BottleLabel;
  index: number;
  src?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const drawCanvas = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Force minimum height of 1200px for high-res export
      const minHeight = 2000;
      const aspectRatio = 3.3;

      // Calculate width based on minimum height and aspect ratio
      const width = minHeight / aspectRatio;
      const height = minHeight;

      // Set canvas size directly (no DPR scaling needed since we're forcing high-res)
      canvas.width = width;
      canvas.height = height;

      try {
        // Load all images
        const boxLabelSrc = label.oldBOXLabel
          ? oldBoxLabel.toString()
          : blankLabel.toString();
        const [bgImg, labelImg, overlayImg] = await Promise.all([
          loadImage(backgroundWhisky),
          src ? loadImage(src) : null,
          loadImage(boxLabelSrc),
        ]);

        // Draw background
        ctx.drawImage(bgImg, 0, 0, width, height);

        // Draw box label overlay at 80% width, 54% top
        const overlayWidth = width * 0.8;
        const overlayHeight =
          (overlayImg.height / overlayImg.width) * overlayWidth;
        const overlayX = (width - overlayWidth) / 2;
        const overlayY = height * 0.54;
        ctx.drawImage(
          overlayImg,
          overlayX,
          overlayY,
          overlayWidth,
          overlayHeight
        );

        // Draw user label at 68% width, 68% top
        if (labelImg) {
          const labelWidth = width * 0.68;
          const labelHeight = (labelImg.height / labelImg.width) * labelWidth;
          const labelX = (width - labelWidth) / 2;
          const labelY = height * 0.68;
          ctx.drawImage(labelImg, labelX, labelY, labelWidth, labelHeight);
        }
      } catch (error) {
        console.error("Error loading images:", error);
      }
    };

    drawCanvas();
  }, [label.oldBOXLabel, src]);

  return (
    <AspectRatio ratio={1 / 3.3} w={isMobile ? "full" : "300px"}>
      <canvas
        className="label-preview-canvas"
        id={`label-preview-canvas-${index}`}
        ref={canvasRef}
        style={{ width: "100%", height: "100%" }}
      />
    </AspectRatio>
  );
};

const LabelSummary = ({ src, isGin, label, index }: LabelSummaryProps) => {
  const isMobile = useMedia("(max-width: 48em)");

  if (isGin) {
    return <GinSummary isMobile={isMobile} src={src} index={index} />;
  }

  return (
    <WhiskySummary isMobile={isMobile} label={label} src={src} index={index} />
  );
};

export default LabelSummary;
